import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import QRCode from 'qrcode';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/contexts/FirebaseAuthContext';
import { useToast } from '@/components/ui/use-toast';
import { ArrowLeft, Download, Palette, QrCode as QrCodeIcon, Link as LinkIcon, Image as ImageIcon, FileText, Share2, Copy, Eye, Loader2, ExternalLink } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { config } from '@/lib/config';

const CreativeAssetsPage = () => {
  const { profile } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [customText, setCustomText] = useState('');
  const [customTextColor, setCustomTextColor] = useState('#ffffff');
  const [customTextFont, setCustomTextFont] = useState('Inter');
  const [customTextStyle, setCustomTextStyle] = useState('normal');
  const [generating, setGenerating] = useState(false);
  const canvasRef = useRef(null);
  const previewSectionRef = useRef(null);
  const [qrCodeDataUrl, setQrCodeDataUrl] = useState('');
  const [showPreviewHighlight, setShowPreviewHighlight] = useState(false);

  // Use production URL for QR codes instead of localhost
  // This ensures the QR code shows the actual live URL that artists will use
  // even during development/testing
  const baseUrl = config.getBaseUrl();
  const artistUrl = profile?.url_slug ? `${baseUrl}/${profile.url_slug}`.toLowerCase() : '';

  useEffect(() => {
    if (!profile) {
      navigate('/admin');
    } else if (profile.url_slug) {
      console.log('Generating QR code for URL:', artistUrl);
      QRCode.toDataURL(artistUrl, {
        errorCorrectionLevel: 'H',
        margin: 2,
        color: {
          dark: '#FFFFFF',
          light: '#00000000'
        }
      })
        .then(url => {
          console.log('QR code generated successfully:', url.substring(0, 50) + '...');
          setQrCodeDataUrl(url);
        })
        .catch(err => console.error('Error generating QR code:', err));
    }
  }, [profile, navigate, artistUrl]);


  const assetTypes = [  {
    id: 'qr-tv-screen',
    title: 'TV Screen Display',
    description: 'Animated 16:9 display for venue TV screens and digital displays.',
    icon: QrCodeIcon,
    color: 'from-purple-500 to-pink-500',
    formats: ['PNG', 'HTML'],
    sizes: ['16:9']
  }, {
    id: 'store',
    title: 'Store',
    description: 'COMING SOON!',
    icon: FileText,
    color: 'from-purple-500 to-pink-500',
    formats: [],
    sizes: []
  }];

  const drawCanvasContent = useCallback(async (canvas) => {
    try {
      const ctx = canvas.getContext('2d');
      const dpr = window.devicePixelRatio || 1;
      const rect = canvas.getBoundingClientRect();

      const width = 1920; // 16:9 ratio for TV screens
      const height = 1080;
      
      canvas.width = width * dpr;
      canvas.height = height * dpr;
      ctx.scale(dpr, dpr);
      
      // Reset canvas style dimensions when switching asset types
      canvas.style.width = `${rect.width}px`;
      canvas.style.height = `${(rect.width * height) / width}px`;

      // Animated gradient background for TV screens - MUCH MORE DRAMATIC
      const time = Date.now() * 0.003; // Faster animation (3x speed)
      const gradient = ctx.createLinearGradient(0, 0, width, height);

      // Create much more vibrant, contrasting animated color stops
      const hue1 = (time * 50) % 360; // Faster hue rotation
      const hue2 = (hue1 + 180) % 360; // Opposite colors for maximum contrast
      const hue3 = (hue1 + 90) % 360; // Perpendicular colors

      // Much more saturated and vibrant colors
      gradient.addColorStop(0, `hsl(${hue1}, 100%, 15%)`); // 100% saturation, darker
      gradient.addColorStop(0.2, `hsl(${hue2}, 100%, 25%)`); // 100% saturation, brighter
      gradient.addColorStop(0.5, `hsl(${hue3}, 100%, 20%)`); // 100% saturation, medium
      gradient.addColorStop(0.8, `hsl(${hue1}, 100%, 30%)`); // 100% saturation, bright
      gradient.addColorStop(1, `hsl(${hue2}, 100%, 15%)`); // 100% saturation, darker

      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, width, height);

      // Add multiple animated overlays for more dramatic effect
      const overlayGradient1 = ctx.createRadialGradient(width * 0.3, height * 0.3, 0, width * 0.3, height * 0.3, height * 0.8);
      overlayGradient1.addColorStop(0, `hsla(${hue1}, 100%, 60%, 0.6)`); // Bright, semi-transparent
      overlayGradient1.addColorStop(1, 'rgba(0, 0, 0, 0.3)');
      ctx.fillStyle = overlayGradient1;
      ctx.fillRect(0, 0, width, height);

      const overlayGradient2 = ctx.createRadialGradient(width * 0.7, height * 0.7, 0, width * 0.7, height * 0.7, height * 0.6);
      overlayGradient2.addColorStop(0, `hsla(${hue2}, 100%, 70%, 0.5)`); // Bright, semi-transparent
      overlayGradient2.addColorStop(1, 'rgba(0, 0, 0, 0.4)');
      ctx.fillStyle = overlayGradient2;
      ctx.fillRect(0, 0, width, height);

      // Add animated moving light effects
      const lightX = (Math.sin(time * 2) * width * 0.3) + width * 0.5;
      const lightY = (Math.cos(time * 1.5) * height * 0.3) + height * 0.5;
      const lightGradient = ctx.createRadialGradient(lightX, lightY, 0, lightX, lightY, height * 0.4);
      lightGradient.addColorStop(0, `hsla(${hue3}, 100%, 80%, 0.4)`); // Bright moving light
      lightGradient.addColorStop(1, 'rgba(0, 0, 0, 0)');
      ctx.fillStyle = lightGradient;
      ctx.fillRect(0, 0, width, height);

      // Add pulsing border effect
      const pulseIntensity = (Math.sin(time * 4) * 0.5 + 0.5) * 0.3; // Pulsing effect
      const borderGradient = ctx.createLinearGradient(0, 0, width, height);
      borderGradient.addColorStop(0, `hsla(${hue1}, 100%, 70%, ${pulseIntensity})`);
      borderGradient.addColorStop(0.5, `hsla(${hue2}, 100%, 70%, ${pulseIntensity})`);
      borderGradient.addColorStop(1, `hsla(${hue3}, 100%, 70%, ${pulseIntensity})`);

      // Draw animated border
      ctx.lineWidth = 20;
      ctx.strokeStyle = borderGradient;
      ctx.strokeRect(10, 10, width - 20, height - 20);

      // TV Screen Layout: Left half for content, right half for QR code

      // Left Half Content - Centered
      const leftHalf = width * 0.5;
      const leftCenter = leftHalf / 2;

      // YOUR SONG REQUEST title on the left (centered)
      ctx.font = 'bold 70px Inter';
      ctx.fillStyle = 'white';
      ctx.textAlign = 'center';
      ctx.shadowColor = 'rgba(59, 130, 246, 0.9)';
      ctx.shadowBlur = 40;
      ctx.shadowOffsetX = 0;
      ctx.shadowOffsetY = 0;
      ctx.fillText('YOUR SONG REQUEST', leftCenter, height * 0.15);
      ctx.shadowBlur = 0;

      // Artist Name on the left (centered)
      ctx.font = 'bold 60px Inter';
      ctx.fillStyle = 'white';
      ctx.textAlign = 'center';
      ctx.shadowColor = 'rgba(59, 130, 246, 0.7)';
      ctx.shadowBlur = 25;
      ctx.fillText(profile.artist_name, leftCenter, height * 0.25);
      ctx.shadowBlur = 0;

      // Custom Text on the left (centered)
      if (customText) {
        ctx.font = `${customTextStyle} 32px ${customTextFont}`;
        ctx.fillStyle = customTextColor;
        ctx.textAlign = 'center';
        ctx.shadowColor = 'rgba(59, 130, 246, 0.7)';
        ctx.shadowBlur = 15;
        ctx.fillText(customText, leftCenter, height * 0.35);
        ctx.shadowBlur = 0;
      }

      // Support messaging on the left (centered)
      ctx.font = 'bold 60px Inter';
      ctx.fillStyle = '#fbbf24';
      ctx.textAlign = 'center';
      ctx.fillText('SUPPORT LIVE MUSIC!', leftCenter, height * 0.55);

      ctx.font = 'bold 45px Inter';
      ctx.fillStyle = '#9ca3af';
      ctx.fillText('SCAN TO REQUEST A SONG WITH', leftCenter, height * 0.7);

      ctx.font = 'bold 55px Inter';
      ctx.fillStyle = '#60a5fa';
      ctx.fillText('YOUR SONG REQUEST', leftCenter, height * 0.85);

      // Right Half: Much Bigger QR Code and Scan Text
      const rightHalf = width * 0.5;
      const qrCodeSize = height * 0.6; // Much bigger QR code (60% of height)
      const qrCodeX = rightHalf + (rightHalf - qrCodeSize) / 2;
      const qrCodeY = height * 0.15; // Moved up to center better

      if (qrCodeDataUrl) {
        // QR Code background
        ctx.fillStyle = 'white';
        ctx.fillRect(qrCodeX - 15, qrCodeY - 15, qrCodeSize + 30, qrCodeSize + 30);

        // QR Code image
        const qrCodeImg = new Image();
        const qrPromise = new Promise((resolve, reject) => {
          qrCodeImg.onload = () => resolve();
          qrCodeImg.onerror = reject;
          qrCodeImg.src = qrCodeDataUrl;
        });

        await qrPromise;
        ctx.drawImage(qrCodeImg, qrCodeX, qrCodeY, qrCodeSize, qrCodeSize);

        // Scan text underneath QR code
        ctx.font = 'bold 45px Inter';
        ctx.fillStyle = '#9ca3af';
        ctx.textAlign = 'center';
        ctx.fillText('SCAN TO REQUEST A SONG!', rightHalf + rightHalf / 2, qrCodeY + qrCodeSize + 80);

        // URL underneath scan text - WHITE TEXT
        const urlText = `yoursongrequest.com/${profile.url_slug}`;
        ctx.font = 'bold 32px Inter';
        ctx.fillStyle = '#ffffff';
        ctx.fillText(urlText, rightHalf + rightHalf / 2, qrCodeY + qrCodeSize + 130);
      } else {
        // Fallback if no QR code
        ctx.font = 'bold 40px Inter';
        ctx.fillStyle = '#ef4444';
        ctx.textAlign = 'center';
        ctx.fillText('QR CODE NOT AVAILABLE', rightHalf + rightHalf / 2, height * 0.5);
        ctx.fillText('Please set your custom URL first', rightHalf + rightHalf / 2, height * 0.5 + 50);
      }

      // YSR in bottom right corner
      ctx.font = 'bold 24px Inter';
      ctx.fillStyle = '#60a5fa';
      ctx.textAlign = 'right';
      ctx.fillText('YSR', width - 50, height - 40);
    } catch (error) {
      console.error('Error in drawCanvasContent:', error);
      throw error;
    }
  }, [profile, qrCodeDataUrl, customText, customTextColor, customTextFont, customTextStyle, selectedAsset]);


  const generateAsset = async (assetType) => {
    setGenerating(true);
    setSelectedAsset(assetType);
    
    setTimeout(async () => {
        try {
            const canvas = canvasRef.current;
            if (canvas) {
                await drawCanvasContent(canvas);
                toast({ title: "Asset Generated!", description: `${assetType.title} is ready for download.` });
                
                // Scroll to the preview section after a short delay to ensure it's rendered
                setTimeout(() => {
                    if (previewSectionRef.current) {
                        previewSectionRef.current.scrollIntoView({ 
                            behavior: 'smooth', 
                            block: 'center',
                            inline: 'nearest'
                        });
                        
                        // Add a brief highlight effect
                        setShowPreviewHighlight(true);
                        setTimeout(() => setShowPreviewHighlight(false), 2000);
                    }
                }, 100);
            }
        } catch (error) {
            console.error('Error generating asset:', error);
            toast({ title: "Error", description: "Could not generate asset. Please try again.", variant: "destructive" });
        } finally {
            setGenerating(false);
        }
    }, 500); // Give a small delay for state to update
  };
  
  useEffect(() => {
    if (selectedAsset && canvasRef.current) {
        drawCanvasContent(canvasRef.current);
    }
  }, [customText, selectedAsset, drawCanvasContent]);

  // Live preview update when custom text styling changes
  useEffect(() => {
    if (selectedAsset && canvasRef.current && customText) {
        drawCanvasContent(canvasRef.current);
    }
  }, [customTextColor, customTextFont, customTextStyle, drawCanvasContent]);

  // Animation loop for TV screen format
  useEffect(() => {
    let animationId;
    
    if (selectedAsset?.id === 'qr-tv-screen' && canvasRef.current) {
      const animate = () => {
        drawCanvasContent(canvasRef.current);
        animationId = requestAnimationFrame(animate);
      };
      animate();
    }
    
    return () => {
      if (animationId) {
        cancelAnimationFrame(animationId);
      }
    };
  }, [selectedAsset, drawCanvasContent]);


  const downloadAsset = (format, size) => {
    if (!selectedAsset) return;

    const canvas = canvasRef.current;
    if (canvas) {
      const link = document.createElement('a');
      const fileName = `${profile.artist_name.replace(/ /g, '_')}-${selectedAsset.id}-${size}`;

      if (format === 'PDF') {
        toast({ title: "Coming Soon!", description: "PDF downloads are not yet available.", variant: "default" });
        return;
      } 
      
      link.download = `${fileName}.${format.toLowerCase()}`;
      link.href = canvas.toDataURL(`image/${format.toLowerCase()}`, 0.95);
      
      link.click();
      toast({ title: "Download Started!", description: `${selectedAsset.title} downloaded successfully.` });
    }
  };

  const copyUrl = () => {
    navigator.clipboard.writeText(artistUrl);
    toast({ title: "Copied!", description: "Your live page URL copied to clipboard" });
  };

  const openInNewWindow = () => {
    const url = canvasRef.current.toDataURL('image/png'); // Assuming PNG for full-screen display
    const newWindow = window.open('', '_blank');
    if (newWindow) {
      newWindow.document.write(`<img src="${url}" alt="Your Song Request" style="width: 100%; height: 100%; object-fit: contain;">`);
      newWindow.document.close();
    }
  };

  if (!profile) return (
    <div className="flex items-center justify-center min-h-screen">
      <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
    </div>
  );

  return (
    <>
      <Helmet><title>Promo Tools - Your Song Request</title><meta name="description" content="Download promotional materials for your events." /></Helmet>
      <TooltipProvider>
        <div className="w-full h-full">
          <main className="w-full h-full p-6">

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className="text-center mb-12">
                <h2 className="text-3xl md:text-4xl font-bold mb-2 multi-glow-2">Your Promo Toolkit</h2>
                <p className="text-gray-400 max-w-2xl mx-auto">
                  Download ready-made materials to promote your live performances and make it easy for your audience to request songs.
                </p>
              </div>

              <Card className="glass-effect border-blue-600 mb-12">
                <CardHeader>
                  <CardTitle className="flex items-center text-blue-300">
                    <LinkIcon className="mr-3" />
                    Your Live Page URL
                  </CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="flex items-center space-x-2 mt-2">
                        <Input
                          value={artistUrl}
                          readOnly
                          className="bg-gray-800 border-gray-700 text-white"
                        />
                        <Button
                          size="icon"
                          variant="outline"
                          onClick={copyUrl}
                          className="text-blue-400 hover:text-blue-300 flex-shrink-0"
                        >
                          <Copy className="w-4 h-4" />
                        </Button>
                        <Button
                          size="icon"
                          variant="outline"
                          onClick={() => window.open(artistUrl, '_blank')}
                          className="text-green-400 hover:text-green-300 flex-shrink-0"
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                    </div>
                </CardContent>
              </Card>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
                {assetTypes.map((asset, index) => (
                  <motion.div
                    key={asset.id}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{once: true}}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                  >
                    <Card className="glass-effect border-gray-800 hover:border-blue-500/50 transition-all duration-300 group h-full flex flex-col text-center">
                      <CardHeader className="items-center">
                        <div className={`w-16 h-16 rounded-lg bg-gradient-to-r ${asset.color} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300`}>
                          <asset.icon className="w-8 h-8 text-white" />
                        </div>
                        <CardTitle className="text-white">{asset.title}</CardTitle>
                      </CardHeader>
                       <CardContent className="flex-grow">
                        <CardDescription className="text-gray-400">
                          {asset.description}
                        </CardDescription>
                      </CardContent>
                      <CardContent>
                        <Button
                          onClick={() => asset.id === 'store' ? null : generateAsset(asset)}
                          disabled={asset.id === 'store' || (generating && selectedAsset?.id === asset.id)}
                          className={`w-full font-bold ${asset.id === 'store' ? 'bg-gray-600 text-gray-300 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700 text-white'}`}
                        >
                          {asset.id === 'store' ? (
                            'Coming Soon!'
                          ) : generating && selectedAsset?.id === asset.id ? (
                            <>
                              <Loader2 className="w-4 h-4 mr-2 animate-spin"/>
                              Creating...
                            </>
                          ) : (
                            <>
                              <Palette className="w-4 h-4 mr-2" />
                              Create
                            </>
                          )}
                        </Button>
                      </CardContent>
                    </Card>
                  </motion.div>
                ))}
              </div>

              <AnimatePresence>
              {selectedAsset && (
                <motion.div
                  ref={previewSectionRef}
                  initial={{ opacity: 0, y: 50, height: 0 }}
                  animate={{ opacity: 1, y: 0, height: 'auto' }}
                  exit={{ opacity: 0, y: 50, height: 0 }}
                  transition={{ duration: 0.7, type: 'spring' }}
                  className="overflow-hidden"
                >
                  <Card className={`glass-effect border-purple-600 transition-all duration-500 ${showPreviewHighlight ? 'ring-4 ring-blue-400 ring-opacity-50 shadow-lg shadow-blue-400/20' : ''}`}>
                    <CardHeader>
                      <CardTitle className="flex items-center text-purple-300">
                        <Palette className="mr-3" />
                        {selectedAsset.title} - Preview & Download
                      </CardTitle>
                      <CardDescription>
                        Customize your asset and download it in various formats.
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="grid lg:grid-cols-2 gap-8">
                        <div>
                          <Label className="text-sm text-gray-400 mb-4 block">Live Preview</Label>
                          <div className="border border-gray-700 rounded-lg overflow-hidden bg-gray-900/50 p-2">
                             <canvas ref={canvasRef} className="w-full h-auto object-contain rounded-md" />
                          </div>
                        </div>

                        <div className="space-y-6">
                          <div>
                            <Label htmlFor="custom-text" className="text-sm font-bold text-gray-300">
                              Custom Text (Optional)
                            </Label>
                            <p className="text-xs text-gray-500 mt-1 mb-2">
                              Add venue details, dates, or a custom message.
                            </p>
                            <Input
                              id="custom-text"
                              value={customText}
                              onChange={(e) => setCustomText(e.target.value)}
                              placeholder="e.g., 'Live at The Blue Note!'"
                              className="bg-gray-800 border-gray-700 text-white mt-2"
                            />
                            
                            {/* Custom Text Styling Options */}
                            {customText && (
                              <div className="mt-4 space-y-3 p-3 bg-gray-800/50 rounded-lg border border-gray-700">
                                <Label className="text-xs font-medium text-gray-400">Text Styling</Label>
                                
                                {/* Color Picker */}
                                <div className="flex items-center space-x-3">
                                  <Label className="text-xs text-gray-400">Color:</Label>
                                  <input
                                    type="color"
                                    value={customTextColor}
                                    onChange={(e) => setCustomTextColor(e.target.value)}
                                    className="w-8 h-8 rounded border border-gray-600 cursor-pointer"
                                  />
                                  <span className="text-xs text-gray-500">{customTextColor}</span>
                                </div>
                                
                                {/* Font Selection */}
                                <div className="flex items-center space-x-3">
                                  <Label className="text-xs text-gray-400">Font:</Label>
                                  <select
                                    value={customTextFont}
                                    onChange={(e) => setCustomTextFont(e.target.value)}
                                    className="bg-gray-700 border border-gray-600 text-white text-xs rounded px-2 py-1"
                                  >
                                    <option value="Inter">Inter</option>
                                    <option value="Arial">Arial</option>
                                    <option value="Helvetica">Helvetica</option>
                                    <option value="Georgia">Georgia</option>
                                    <option value="Times New Roman">Times New Roman</option>
                                    <option value="Courier New">Courier New</option>
                                  </select>
                                </div>
                                
                                {/* Typographic Styles */}
                                <div className="flex items-center space-x-3">
                                  <Label className="text-xs text-gray-400">Style:</Label>
                                  <div className="flex space-x-2">
                                    <Button
                                      size="sm"
                                      variant={customTextStyle === 'normal' ? 'default' : 'outline'}
                                      onClick={() => setCustomTextStyle('normal')}
                                      className="text-xs px-2 py-1 h-6"
                                    >
                                      Normal
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant={customTextStyle === 'bold' ? 'default' : 'outline'}
                                      onClick={() => setCustomTextStyle('bold')}
                                      className="text-xs px-2 py-1 h-6"
                                    >
                                      Bold
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant={customTextStyle === 'italic' ? 'default' : 'outline'}
                                      onClick={() => setCustomTextStyle('italic')}
                                      className="text-xs px-2 py-1 h-6"
                                    >
                                      Italic
                                    </Button>
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>

                          <div>
                             <Label className="text-sm font-bold text-gray-300 mb-3 block">
                              Download Options
                             </Label>
                             <div className="space-y-3">
                               {selectedAsset.formats.map((format) => (
                                 <div key={format} className="space-y-2">
                                   <h4 className="text-sm font-medium text-gray-300">{format} Format</h4>
                                   <div className="flex flex-wrap gap-2">
                                     {selectedAsset.sizes.map((size) => (
                                       <div key={`${format}-${size}`} className="flex gap-2">
                                         <Button
                                           size="sm"
                                           variant="outline"
                                           onClick={() => downloadAsset(format, size)}
                                           className="text-green-400 border-green-500/30 hover:bg-green-500/10 hover:text-green-300"
                                         >
                                           <Download className="w-4 h-4 mr-2" />
                                           {size} ({format})
                                         </Button>
                                         
                                         {/* Open In New Window option for HTML format */}
                                         {format === 'HTML' && (
                                           <Button
                                             size="sm"
                                             variant="outline"
                                             onClick={() => openInNewWindow()}
                                             className="text-blue-400 border-blue-500/30 hover:bg-blue-500/10 hover:text-blue-300"
                                           >
                                             <ExternalLink className="w-4 h-4 mr-2" />
                                             Open Full Screen
                                           </Button>
                                         )}
                                       </div>
                                     ))}
                                   </div>
                                 </div>
                               ))}
                             </div>
                           </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              )}
              </AnimatePresence>
            </motion.div>
          </main>
        </div>
      </TooltipProvider>
    </>
  );
};

export default CreativeAssetsPage;
